import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation } from 'lucide-react';
import L from 'leaflet';
const areas = [{
  name: 'Philadelphia County',
  lat: 39.9526,
  lng: -75.1652,
  type: 'primary' as const
}, {
  name: 'Bensalem, PA',
  lat: 40.1046,
  lng: -74.9468,
  type: 'primary' as const
}, {
  name: 'Ridley Park',
  lat: 39.8801,
  lng: -75.3249,
  type: 'primary' as const
}, {
  name: 'Delaware County',
  lat: 39.9167,
  lng: -75.3963,
  type: 'secondary' as const
}, {
  name: 'Montgomery County',
  lat: 40.2116,
  lng: -75.3697,
  type: 'secondary' as const
}, {
  name: 'Chester County',
  lat: 39.9623,
  lng: -75.7497,
  type: 'secondary' as const
}, {
  name: 'Lancaster County',
  lat: 40.0468,
  lng: -76.1784,
  type: 'extended' as const
}, {
  name: 'Reading',
  lat: 40.3357,
  lng: -75.9269,
  type: 'extended' as const
}, {
  name: 'Schuylkill County',
  lat: 40.7062,
  lng: -76.2097,
  type: 'extended' as const
}];
function ServiceMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [40.1, -75.5],
      zoom: 8,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false
    });
    L.control.zoom({
      position: 'bottomright'
    }).addTo(map);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19
    }).addTo(map);
    // Draw service area polygon
    const polygonCoords: L.LatLngExpression[] = [[40.85, -76.45], [40.45, -74.8], [39.8, -74.85], [39.78, -75.55], [39.85, -76.05], [40.0, -76.4]];
    L.polygon(polygonCoords, {
      color: '#C9A84C',
      weight: 2,
      fillColor: '#C9A84C',
      fillOpacity: 0.08,
      dashArray: '6, 8'
    }).addTo(map);
    // Custom marker icon
    const createIcon = (type: string) => {
      const color = type === 'primary' ? '#C9A84C' : type === 'secondary' ? '#2B7DE9' : '#64748B';
      const size = type === 'primary' ? 14 : type === 'secondary' ? 11 : 9;
      return L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.8);
          box-shadow: 0 0 12px ${color}66, 0 0 24px ${color}33;
        "></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });
    };
    areas.forEach(area => {
      const marker = L.marker([area.lat, area.lng], {
        icon: createIcon(area.type)
      }).addTo(map);
      marker.bindTooltip(area.name, {
        className: 'custom-tooltip',
        direction: 'top',
        offset: [0, -10]
      });
    });
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  return <div ref={mapRef} className="w-full h-full rounded-2xl" data-id="element-357" />;
}
export function ServiceAreas() {
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);
  const typeLabels = {
    primary: {
      label: 'Core Areas',
      color: 'bg-gold'
    },
    secondary: {
      label: 'Extended Coverage',
      color: 'bg-brand'
    },
    extended: {
      label: 'Regional Reach',
      color: 'bg-muted'
    }
  };
  return <section id="areas" className="py-32 bg-navy relative overflow-hidden" data-id="element-358">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(#FFFFFF 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-359"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-360">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-361">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-362">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-8 border border-white/10" data-id="element-363">
              <MapPin className="w-8 h-8 text-gold" data-id="element-364" />
            </div>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-8" data-id="element-365">
              Proudly Serving Your Community
            </h2>
            <p className="text-xl text-gray-400 italic font-heading font-light" data-id="element-366">
              "These aren't just service areas. These are our neighbors. Our
              community. Our home too."
            </p>
          </motion.div>
        </div>

        {/* Map + Areas Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start" data-id="element-367">
          {/* Interactive Map */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-3 h-[400px] md:h-[500px] rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]" data-id="element-368">
            <ServiceMap data-id="element-369" />
          </motion.div>

          {/* Area List */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-2 space-y-10" data-id="element-370">
            {(['primary', 'secondary', 'extended'] as const).map(type => <div key={type} data-id="element-371">
                <div className="flex items-center gap-3 mb-5" data-id="element-372">
                  <div className={`w-2.5 h-2.5 rounded-full ${typeLabels[type].color}`} data-id="element-373"></div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400" data-id="element-374">
                    {typeLabels[type].label}
                  </span>
                </div>
                <div className="space-y-2" data-id="element-375">
                  {areas.filter(a => a.type === type).map((area, index) => <motion.div key={area.name} initial={{
                opacity: 0,
                x: 10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.05
              }} onMouseEnter={() => setHoveredArea(area.name)} onMouseLeave={() => setHoveredArea(null)} className={`flex items-center gap-4 px-5 py-3.5 rounded-xl border transition-all duration-300 cursor-default ${hoveredArea === area.name ? 'bg-white/10 border-gold/40 shadow-[0_0_15px_rgba(201,168,76,0.1)]' : 'bg-white/[0.03] border-white/5 hover:bg-white/[0.06] hover:border-white/10'}`} data-id="element-376">
                        <Navigation size={14} className={`flex-shrink-0 transition-colors duration-300 ${hoveredArea === area.name ? 'text-gold' : 'text-gray-500'}`} data-id="element-377" />
                        <span className={`font-medium tracking-wide text-sm transition-colors duration-300 ${hoveredArea === area.name ? 'text-white' : 'text-gray-300'}`} data-id="element-378">
                          {area.name}
                        </span>
                      </motion.div>)}
                </div>
              </div>)}

            {/* CTA */}
            <div className="pt-6 border-t border-white/10" data-id="element-379">
              <p className="text-gray-400 text-sm font-light mb-4" data-id="element-380">
                Don't see your area? We may still be able to help.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 text-gold text-sm font-bold uppercase tracking-widest hover:text-white transition-colors duration-300" data-id="element-381">
                <span data-id="element-382">Contact Us</span>
                <span data-id="element-383">→</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Leaflet tooltip styles */}
      <style data-id="element-384">{`
        .custom-tooltip {
          background: #1B2A4A !important;
          color: #fff !important;
          border: 1px solid rgba(201, 168, 76, 0.3) !important;
          border-radius: 8px !important;
          padding: 6px 12px !important;
          font-family: 'DM Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          letter-spacing: 0.05em !important;
          box-shadow: 0 8px 20px rgba(0,0,0,0.4) !important;
        }
        .custom-tooltip::before {
          border-top-color: #1B2A4A !important;
        }
        .leaflet-control-zoom a {
          background: #1B2A4A !important;
          color: #C9A84C !important;
          border-color: rgba(255,255,255,0.1) !important;
        }
        .leaflet-control-zoom a:hover {
          background: #15223D !important;
        }
      `}</style>
    </section>;
}